import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { adminApi } from './adminApi';

const styles = `
  .usage-totals {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 28px;
  }
  .usage-card {
    background: #fff;
    border-radius: 12px;
    padding: 22px 24px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  }
  .usage-card-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 8px;
  }
  .usage-card-value {
    font-size: 1.8rem;
    font-weight: 700;
    color: #2C2C2C;
    letter-spacing: -0.03em;
    line-height: 1;
  }
  .usage-card-sub {
    font-size: 0.8rem;
    color: #888;
    margin-top: 5px;
  }
  .section-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #2C2C2C;
    margin-bottom: 14px;
    letter-spacing: -0.01em;
  }
  .pricing-note {
    font-size: 0.8rem;
    color: #aaa;
    margin-bottom: 16px;
    line-height: 1.5;
  }
  .data-table {
    width: 100%;
    border-collapse: collapse;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    overflow: hidden;
  }
  .data-table th {
    text-align: left;
    padding: 12px 16px;
    font-size: 0.78rem;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #EDE9E4;
    background: #FAFAF8;
  }
  .data-table td {
    padding: 13px 16px;
    font-size: 0.9rem;
    color: #2C2C2C;
    border-bottom: 1px solid #F0EDE8;
  }
  .data-table tr:last-child td { border-bottom: none; }
  .loading-state { color: #888; font-size: 0.9rem; padding: 20px 0; }
  .error-state { color: #C0392B; font-size: 0.9rem; padding: 20px 0; }
`;

function fmt(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}

export default function AdminUsage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi.usage()
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const sorted = data
    ? [...data.per_user].sort((a, b) => b.estimated_cost_usd - a.estimated_cost_usd)
    : [];

  return (
    <AdminLayout title="Usage & Costs">
      <style>{styles}</style>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {data && (
        <>
          <div className="usage-totals">
            <div className="usage-card">
              <div className="usage-card-label">Input Tokens</div>
              <div className="usage-card-value">{fmt(data.total_input_tokens)}</div>
              <div className="usage-card-sub">This month</div>
            </div>
            <div className="usage-card">
              <div className="usage-card-label">Output Tokens</div>
              <div className="usage-card-value">{fmt(data.total_output_tokens)}</div>
              <div className="usage-card-sub">This month</div>
            </div>
            <div className="usage-card">
              <div className="usage-card-label">Total Tokens</div>
              <div className="usage-card-value">{fmt(data.total_tokens)}</div>
              <div className="usage-card-sub">This month</div>
            </div>
            <div className="usage-card">
              <div className="usage-card-label">Estimated Cost</div>
              <div className="usage-card-value">${(data.estimated_cost_usd || 0).toFixed(4)}</div>
              <div className="usage-card-sub">USD — claude-haiku-4-5 rate</div>
            </div>
          </div>

          {data.content_search && Object.keys(data.content_search).length > 0 && (
            <>
              <div className="section-title">Content Search</div>
              <div className="usage-totals" style={{marginBottom: 28}}>
                {Object.entries(data.content_search).map(([svc, info]) => (
                  <React.Fragment key={svc}>
                    <div className="usage-card">
                      <div className="usage-card-label">{svc.replace(/_/g, ' ')} calls</div>
                      <div className="usage-card-value">{info.calls}</div>
                      <div className="usage-card-sub">This month</div>
                    </div>
                    <div className="usage-card">
                      <div className="usage-card-label">{svc.replace(/_/g, ' ')} cost</div>
                      <div className="usage-card-value">${(info.estimated_cost_usd || 0).toFixed(4)}</div>
                      <div className="usage-card-sub">USD — $5/1K queries</div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </>
          )}

          {data.intake_mia && data.intake_mia.total_tokens > 0 && (
            <>
              <div className="section-title">Mia Intake Usage</div>
              <div className="usage-totals" style={{marginBottom: 28}}>
                <div className="usage-card">
                  <div className="usage-card-label">Intake Input</div>
                  <div className="usage-card-value">{fmt(data.intake_mia.input_tokens)}</div>
                  <div className="usage-card-sub">This month</div>
                </div>
                <div className="usage-card">
                  <div className="usage-card-label">Intake Output</div>
                  <div className="usage-card-value">{fmt(data.intake_mia.output_tokens)}</div>
                  <div className="usage-card-sub">This month</div>
                </div>
                <div className="usage-card">
                  <div className="usage-card-label">Intake Total</div>
                  <div className="usage-card-value">{fmt(data.intake_mia.total_tokens)}</div>
                  <div className="usage-card-sub">This month</div>
                </div>
                <div className="usage-card">
                  <div className="usage-card-label">Intake Cost</div>
                  <div className="usage-card-value">${(data.intake_mia.estimated_cost_usd || 0).toFixed(4)}</div>
                  <div className="usage-card-sub">USD — claude-haiku-4-5 rate</div>
                </div>
              </div>
            </>
          )}

          <div className="section-title">Per-User Breakdown</div>
          <div className="pricing-note">
            Pricing: claude-haiku-4-5 = $0.25/1M input · $1.25/1M output
            &nbsp;|&nbsp; claude-sonnet-4-6 = $3/1M input · $15/1M output
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Input Tokens</th>
                <th>Output Tokens</th>
                <th>Total Tokens</th>
                <th>Est. Cost</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(u => (
                <tr key={u.family_id}>
                  <td style={{ fontWeight: 600 }}>{u.name}</td>
                  <td style={{ color: '#666' }}>{u.email}</td>
                  <td>{fmt(u.input_tokens)}</td>
                  <td>{fmt(u.output_tokens)}</td>
                  <td>{fmt(u.total_tokens)}</td>
                  <td style={{ fontWeight: 600, color: '#E8845A' }}>
                    ${(u.estimated_cost_usd || 0).toFixed(4)}
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ color: '#888', textAlign: 'center', padding: '24px' }}>
                    No usage data for this month.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </AdminLayout>
  );
}
